import { Fragment, forwardRef, useEffect, useMemo, useRef } from "react";

import Pagination, { type PaginationProps } from "@/components/pagination";
import Review from "@/components/review-modal/review";
import {
  type ReviewsPaginationType,
  SortByOptions,
  type SortByType,
} from "@/components/review-modal/types";
import { Separator } from "@/components/ui/separator";

import type { GameType } from "../types";
import SortMenu, { DEFAULT_SORT } from "./sort-menu";

export type ReviewsRef = HTMLDivElement;

interface ReviewsProps extends ReviewsPaginationType {
  game: GameType;
  onFocusRequest(): void;
  path: string[];
}

export default forwardRef<ReviewsRef, ReviewsProps>(function Reviews(
  { game, count = 0, objects, onFocusRequest, page = 1, path, pages },
  ref
) {
  const previousPage = useRef(0);
  const previousSort = useRef<SortByType>(DEFAULT_SORT);

  const selectedSortOption = useMemo(() => {
    let option: SortByType = DEFAULT_SORT;
    for (const s of path) {
      if (s.startsWith("reviews:sort:")) {
        const tmp = s.slice(13);
        if (tmp in SortByOptions) {
          option = tmp as SortByType;
        }
      }
    }
    return option;
  }, [path]);

  useEffect(() => {
    const pageChanged = previousPage.current && previousPage.current !== page;
    const sortChanged =
      previousSort.current && previousSort.current !== selectedSortOption;

    if (pageChanged || sortChanged) {
      onFocusRequest();
    }

    previousPage.current = page;
    previousSort.current = selectedSortOption;
  }, [page, selectedSortOption, onFocusRequest]);

  if (!objects.length) {
    return null;
  }

  const pathPrefix = "reviews:page:";

  const paginationProps: PaginationProps = {
    count,
    page,
    pages,
    path: path.filter((s) => !s.startsWith(pathPrefix)),
    pathPrefix,
    perPage: 10,
  };

  return (
    <section className="mb-5 scroll-mt-50 pt-5" ref={ref}>
      <div className="mb-6.5 flex items-center justify-between md:mb-7.5">
        <h3 className="text-20 text-white leading-[1.2em] md:text-24">
          Reviews
        </h3>
        <SortMenu path={path} selected={selectedSortOption} />
      </div>
      <div className="flex flex-col items-stretch gap-5 md:gap-0">
        {objects.map((review, index) => (
          <Fragment key={review.id}>
            {!!index && <Separator className="bg-white/10" />}
            <Review
              className="md:-mx-5"
              game={game}
              path={path}
              review={review}
            />
          </Fragment>
        ))}
      </div>
      <Pagination className="mt-[7px] md:mt-12" {...paginationProps} />
    </section>
  );
});
